import {
  Typography,
  TableRow,
  TableCell,
  Grid,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { KeyValue, Query, TableHeader } from "../../interfaces";
import MultiHeaderTable from "../MultiHeaderTable/MultiHeaderTable";
import React, { useState } from "react";
import { getStateName, selectedStatesAlpha } from "../../refdata/FilterOptions";
import { sortObjValue } from "../../common/services/Utilities";

const PREFIX = "SelectedMarketTable";

const classes = {
  dialog: `${PREFIX}-dialog`,
  dialogButton: `${PREFIX}-dialogButton`,
  dialogTitle: `${PREFIX}-dialogTitle`,
  dialogContent: `${PREFIX}-dialogContent`,
};

const Root = styled("div")(({ theme }) => ({
  [`& .${classes.dialog}`]: {
    '& div[role="dialog"]': {
      padding: "32px 32px 8px 32px",
      maxWidth: "500px",
    },
    "& .MuiDialogContentText-root": {
      marginBottom: 0,
      paddingBottom: 0,
    },
    "& .MuiDialogContent-root": {
      padding: 0,
    },
  },
  [`& .${classes.dialogContent}`]: {
    padding: 0,
  },
}));

interface Props {
  appState: Query;
  updateStates: (key: string, value: string[]) => void;
  updateMarket: (key: string, value: KeyValue[]) => void;
}

export default function SelectedMarketTable({
  appState,
  updateStates,
  updateMarket,
}: Props) {
  const [openModal, setOpenModal] = useState(false);
  const largeDownloadAlert = (
    <Dialog
      id="clear-all-alert"
      className={classes.dialog}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      open={openModal}
    >
      <DialogContent>
        <DialogContentText
          variant="body2"
          color="primary.main"
          id="alert-dialog-description"
        >
          Clearing the Selected Market will remove all locational filtering from
          your report. You can specify new locational criteria using the search
          panel, then re-generate your report.
        </DialogContentText>
        <DialogTitle
          sx={{
            textAlign: "center",

            color: "#1A4480",
            fontSize: 24,
          }}
          id="alert-dialog-title"
        >
          Do you wish to continue?
        </DialogTitle>
      </DialogContent>
      <DialogActions
        sx={{
          justifyContent: "center",
        }}
      >
        <Grid container spacing={2}>
          <Grid size={6}>
            <Button
              className={classes.dialogButton}
              onClick={() => setOpenModal(false)}
              color="secondary"
              variant="contained"
              sx={{
                width: 170,
              }}
            >
              Cancel
            </Button>
          </Grid>
          <Grid size={6}>
            <Button
              className={classes.dialogButton}
              onClick={() => {
                updateStates("stateNames", [""]);
                updateMarket("marketSelection", [{}]);
                setOpenModal(false);
              }}
              variant="contained"
              sx={{
                "width": 170,

                "&:hover, &:focus": {
                  backgroundColor: "#fff !important",
                  color: "#1a4480",
                  boxShadow: "none",
                },
              }}
            >
              Continue
            </Button>
          </Grid>
        </Grid>
      </DialogActions>
    </Dialog>
  );
  const marketSelectionHeaderMetaData: KeyValue[] = [
    { title: "State", apiField: "", styles: { backgroundColor: "#1f7b7b" } },
    { title: "Counties", apiField: "", styles: { backgroundColor: "#1f7b7b" } },
    { title: "Cities", apiField: "", styles: { backgroundColor: "#1f7b7b" } },
    {
      title: "ZIP Codes",
      apiField: "",
      styles: { backgroundColor: "#1f7b7b" },
    },
  ];
  const marketSelectionHeaders: TableHeader[][] = [
    marketSelectionHeaderMetaData.map((element) => ({
      title: element.title,
      styles: element.styles,
      colSpan: 1,
      apiField: element.apiField,
    })),
  ];

  React.useEffect(() => {
    if (
      appState.filters?.stateNames &&
      appState.filters.stateNames.length !==
        appState.ui.marketSelection.length &&
      process.env.NODE_ENV !== "test"
    ) {
      updateMarket(
        "marketSelection",
        appState.ui.marketSelection.slice(
          0,
          appState.filters.stateNames.length,
        ),
      );
    }
  }, [appState.filters.stateNames, appState.ui.marketSelection, updateMarket]);

  function createData(
    State: string,
    Counties: string[],
    Cities: string[],
    Zips: string[],
  ) {
    return {
      State: State || "All",
      Counties: Counties || "All",
      Cities: Cities || "All",
      Zips: Zips || "All",
    };
  }
  const validMarketSelection = appState.ui?.marketSelection
    ? appState.ui.marketSelection.every((item: KeyValue) => item.counties)
    : false;
  const sortedStateNames = appState.filters.stateNames
    ? appState.filters.stateNames.map((state: string) =>
        getStateName(selectedStatesAlpha, state),
      )
    : [];
  const rows =
    appState.filters.stateNames?.[0] !== "" &&
    validMarketSelection &&
    appState.filters.stateNames.length === appState.ui.marketSelection.length &&
    sortedStateNames.map((row: string, index: number) => {
      return createData(
        row,
        row !== "" &&
          appState.ui.marketSelection &&
          appState.ui.marketSelection[index]?.counties.join(", "),
        row !== "" &&
          appState.ui.marketSelection &&
          appState.ui.marketSelection[index]?.cities.join(", "),
        row !== "" &&
          appState.ui.marketSelection &&
          appState.ui.marketSelection[index]?.zips.join(", "),
      );
    });
  return (
    <Root>
      {largeDownloadAlert}
      {rows && appState.ui.locations === "marketSelect" && (
        <>
          <Grid container>
            <Grid size={10}>
              <Typography variant="h4">Selected Market</Typography>
            </Grid>
            <Grid size={2}>
              <Button
                style={{ float: "right" }}
                variant="contained"
                disableElevation
                color="secondary"
                onClick={() => setOpenModal(true)}
              >
                Clear all
              </Button>
            </Grid>
          </Grid>
          <MultiHeaderTable
            tableBody={
              <>
                {sortObjValue(rows, "State").map((row: KeyValue, i: number) => {
                  return (
                    row.State !== "" && (
                      <TableRow id={`marketSelectionRow-${i}`} key={i}>
                        <TableCell id={`state-value-${i}`} align="right">
                          {row.State}
                        </TableCell>
                        <TableCell id={`county-value-${i}`} align="right">
                          {row.Counties}
                        </TableCell>
                        <TableCell id={`city-value-${i}`} align="right">
                          {row.Cities}
                        </TableCell>
                        <TableCell id={`zip-value-${i}`} align="right">
                          {row.Zips}
                        </TableCell>
                      </TableRow>
                    )
                  );
                })}
              </>
            }
            tableHeaders={marketSelectionHeaders}
          />
        </>
      )}
    </Root>
  );
}
